import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

export type InputProps = VariantProps<typeof inputVariants> &
  Omit<React.InputHTMLAttributes<HTMLInputElement>, "size" | "autoComplete"> & {
    autoComplete?: boolean;
    invalid?: boolean;
    disabledOnWheel?: boolean;
  };

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      variant,
      size,
      type,
      autoComplete = false,
      invalid = false,
      disabledOnWheel = false,
      ...props
    },
    ref
  ) => {
    return (
      <input
        type={type}
        className={cn(
          invalid ? "border-red-dark-400" : "border-gray-dark-200",
          inputVariants({ variant, size, className })
        )}
        ref={ref}
        onWheel={disabledOnWheel ? (e) => (e.target as HTMLInputElement).blur() : undefined}
        {...(!autoComplete
          ? {
              autoComplete: type === "password" ? "new-password" : "off"
            }
          : {})}
        {...props}
      />
    );
  }
);

Input.displayName = "Input";

const inputVariants = cva(
  "flex w-full rounded-[8px] border bg-input subtext-small text-foreground ring-offset-background file:border-0 file:bg-transparent file:label-small placeholder:text-gray-dark-2200 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50",
  {
    variants: {
      variant: {
        default: "bg-background",
        secondary: "bg-gray-dark-600",
        transparent:
          "bg-black border-none focus-visible:ring-0 focus-visible:ring-offset-0 text-foreground"
      },
      size: {
        default: "px-3 py-[10px] h-10",
        sm: "py-2 h-9",
        xs: "py-[6px] h-8"
      }
    },
    defaultVariants: {
      variant: "default",
      size: "default"
    }
  }
);

export { Input };
